import { setWithinPercentageRange } from "@@/utilities/set-within-range";
import { colorToHsla } from "./color-conversions/color-to-hsla";
import { hslaToRgba } from "./color-conversions/hsla-to-rgba";
import { rgbaToHex } from "./color-conversions/rgba-to-hex";
import { rgbaToString } from "./color-conversions/rgba-to-string";
import type { ColorValue } from "./color-value";
import type { HexColor } from "./hex-color/hex-color";
import type { HSL } from "./hsla-color/hsl-color";
import type { HSLA } from "./hsla-color/hsla-color";
import { toHslaString } from "./hsla-color/to-hsla-string";
import { isDarkColor } from "./is-dark-color";

const _withModifications = (current: HSLA) => (hsla: Partial<HSLA>) =>
    colorValueFactory({
        hue: hsla.hue ?? current.hue,
        saturation: hsla.saturation ?? current.saturation,
        light: hsla.light ?? current.light,
        alpha: hsla.alpha ?? current.alpha,
    });
const _withRelativeModifications = (current: HSLA) => (hsla: Partial<HSLA>) =>
    colorValueFactory({
        hue: hsla.hue ? (current.hue + hsla.hue) % 360 : current.hue,
        saturation: hsla.saturation
            ? setWithinPercentageRange(current.saturation + hsla.saturation)
            : current.saturation,
        light: hsla.light
            ? setWithinPercentageRange(current.light + hsla.light)
            : current.light,
        alpha: hsla.alpha
            ? setWithinPercentageRange(current.alpha + hsla.alpha)
            : current.alpha,
    });

const _withAlpha = (current: HSLA) => {
    const withMods = _withModifications(current);
    return (alpha: number) => withMods({ alpha });
};
const _withHue = (current: HSLA) => {
    const withMods = _withModifications(current);
    return (hue: number) => withMods({ hue });
};
const _withLight = (current: HSLA) => {
    const withMods = _withModifications(current);
    return (light: number) => withMods({ light });
};
const _withSaturation = (current: HSLA) => {
    const withMods = _withModifications(current);
    return (saturation: number) => withMods({ saturation });
};

const colorValueCache = new Map<string, ColorValue>();

const colorValueFactory = (
    color:
        | (HSL & {
              alpha?: number;
          })
        | string
        | HexColor,
): ColorValue => {
    const hslOptionalAlpha =
        typeof color === "string" ? colorToHsla(color) : color;
    const { hue, saturation, light } = hslOptionalAlpha;
    const alpha = hslOptionalAlpha.alpha ?? 1;
    const hslaColor: HSLA = { hue, saturation, light, alpha };
    const hslaString = toHslaString({ hue, saturation, light, alpha });
    const cachedValue = colorValueCache.get(hslaString);
    if (cachedValue) return cachedValue;

    // Validate input
    if (hue < 0 || hue > 360)
        throw new Error("Hue must be within range 0 - 360");
    if (saturation < 0 || saturation > 1)
        throw new Error("Saturation must be within range 0 - 1");
    if (light < 0 || light > 1)
        throw new Error(
            `Light must be within range 0 - 1 not ${light.toPrecision()}; ${JSON.stringify(
                color,
            )} `,
        );
    if (alpha < 0 || alpha > 1)
        throw new Error("Alpha must be within range 0 - 1");

    // Calculate color values
    const rgba = hslaToRgba(hue, saturation, light, alpha);
    const rgbaString = rgbaToString(rgba);
    const hexColor = rgbaToHex(rgba);

    // Determine if the color is dark or light
    const dark = isDarkColor(rgba);
    const colorValue: ColorValue = {
        _type: "COLOR_VALUE",
        asCssHsla: hslaString,
        asRgba: rgbaString,
        rgba,
        asHex: hexColor,
        asString: hslaString,
        isLight: !dark,
        isDark: dark,
        hue,
        saturation,
        light,
        alpha,
        withAlpha: _withAlpha(hslaColor), // (value: number) => withModifications({ alpha: value }),
        withHue: _withHue(hslaColor), // (value: number) => withModifications({ hue: value }),
        withLight: _withLight(hslaColor), // (value: number) => withModifications({ light: value }),
        withSaturation: _withSaturation(hslaColor), // (value: number) =>
        withModifications: _withModifications(hslaColor),
        withRelativeModifications: _withRelativeModifications(hslaColor),
    } as unknown as ColorValue;

    colorValueCache.set(hslaString, colorValue);
    return colorValue;
};

export { colorValueFactory };
